import { Check, Close } from '@mui/icons-material';
import { Box, ButtonBase, IconButton, Stack } from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import { ReactNode, useState } from 'react';
import { useDisclosure } from '@shared/hooks';
import { SystemStyleObject } from '@mui/system';

type ButtonsPlacement = 'bottomRight' | 'right';

interface EditableValueProps<T> {
  editable: boolean;
  renderValue: () => ReactNode;
  renderInput: (props: {
    value: T | null | Partial<T>;
    onChange: (value: T | null | Partial<T>) => void;
    error?: string;
  }) => ReactNode;
  onSave: (value: T) => Promise<void> | void;
  defaultValue: T | null;
  validate?: (value: T | null | Partial<T>) => string | undefined;
  buttonsPlacement?: ButtonsPlacement;
}

export function EditableValue<T>({
  editable,
  renderValue,
  renderInput,
  onSave,
  defaultValue,
  validate,
  buttonsPlacement = 'bottomRight',
}: EditableValueProps<T>) {
  const t = useAppTFunction();
  const editMode = useDisclosure(false);
  const [value, setValue] = useState<T | null | Partial<T>>(defaultValue);
  const [error, setError] = useState<string>();

  const handleSave = async () => {
    const validationError = validate?.(value);
    if (validationError) {
      setError(validationError);
      return;
    }

    await onSave(value as T);
    editMode.onClose();
  };

  const handleClose = () => {
    setValue(defaultValue);
    setError(undefined);
    editMode.onClose();
  };

  const onChange = (value: T | null | Partial<T>) => {
    setValue(value);
    const validationError = validate?.(value);
    setError(validationError);
  };

  const onOpen = () => {
    setValue(defaultValue);
    setError(undefined);
    editMode.onOpen();
  };

  if (!editable) {
    return renderValue();
  }

  const { wrapper, buttons } = buttonsSxMap[buttonsPlacement];

  return (
    <Box display="grid" gridTemplateAreas="'stack'">
      <Stack
        sx={wrapper}
        gridArea="stack"
        visibility={editMode.isOpen ? 'visible' : 'hidden'}
        justifyContent="start"
      >
        <Box flex={1}>{renderInput({ value, onChange, error })}</Box>
        <Stack direction="row" spacing={0.5} sx={buttons}>
          <IconButton
            onClick={handleClose}
            sx={{ p: 0.5, borderRadius: 1 }}
            aria-label={t('common.close')}
          >
            <Close fontSize="small" />
          </IconButton>
          <IconButton
            onClick={handleSave}
            sx={{ p: 0.5, borderRadius: 1 }}
            aria-label={t('common.save')}
          >
            <Check fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
      <ButtonBase
        onClick={onOpen}
        sx={{
          p: 0.5,
          borderRadius: 1,
          cursor: 'pointer',
          fontSize: 'inherit',
          fontWeight: 'inherit',
          gridArea: 'stack',
          visibility: editMode.isOpen ? 'hidden' : 'visible',
          justifyContent: 'start',
          width: 'fit-content',
          height: 'fit-content',
          '&:hover,&:focus-visible': { bgcolor: 'divider', textAlign: 'left' },
        }}
      >
        {renderValue()}
      </ButtonBase>
    </Box>
  );
}

const buttonsSxMap: Record<
  ButtonsPlacement,
  { wrapper: SystemStyleObject; buttons?: SystemStyleObject }
> = {
  bottomRight: {
    wrapper: {
      position: 'relative',
    },
    buttons: {
      position: 'absolute',
      right: 0,
      bottom: '-30px',
    },
  },
  right: {
    wrapper: {
      flexDirection: 'row',
      alignItems: 'start',
      gap: 1,
    },
    buttons: {
      marginTop: '6px',
    },
  },
};
